import type { ElementType, ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { PlanIcon } from './PlanIcon';

/**
 * A module in the plan editor: its mark, name and one line, a control at the
 * end (the switch, or "Included"), and its settings folded underneath.
 */
export function PlanModuleBlock({
  Icon,
  title,
  description,
  control,
  expanded,
  onToggleExpand,
  configureLabel,
  dimmed,
  children,
}: {
  Icon: ElementType;
  title: string;
  description: string;
  control: ReactNode;
  expanded: boolean;
  onToggleExpand: () => void;
  configureLabel: string;
  /** The module is switched off: its settings still edit, but read as inactive. */
  dimmed?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="py-4 first:pt-0 last:pb-0">
      <div className="flex items-center gap-3">
        <PlanIcon Icon={Icon} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{title}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        {control}
        <Button variant="ghost" size="icon-sm" aria-label={configureLabel} aria-expanded={expanded} onClick={onToggleExpand}>
          <ChevronDown className={cn('text-muted-foreground transition-transform', expanded && 'rotate-180')} />
        </Button>
      </div>
      {expanded && <div className={cn('mt-4 sm:ps-11', dimmed && 'opacity-60')}>{children}</div>}
    </div>
  );
}
